import { useCallback, useEffect, useMemo, useState } from "react";
import { Activity, Cpu, Gauge, AlertTriangle, Timer, RefreshCw, Radio } from "lucide-react";
import { ResponsiveContainer, ComposedChart, Bar, Line, Area, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import HelpButton from "@/components/HelpButton";
import LoadAlertCard from "@/components/admin/LoadAlertCard";

const REFRESH_MS = 30000;
const fmtClock = (iso) => new Date(iso).toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" });
const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString("nl-NL", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "—");

// Vul ontbrekende minuten (zonder verkeer) met nullen zodat de grafiek doorloopt.
function fillMinutes(buckets, hours) {
  const byMin = new Map(buckets.map((b) => [new Date(b.minute).getTime(), b]));
  const end = Math.floor(Date.now() / 60000) * 60000;
  const out = [];
  for (let ts = end - hours * 3600000; ts <= end; ts += 60000) {
    const b = byMin.get(ts) || {};
    out.push({ ts, label: fmtClock(new Date(ts).toISOString()), count: b.count || 0, avg_ms: b.avg_ms || 0, p95_ms: b.p95_ms || 0,
               errors: b.errors || 0, cpu_pct: b.cpu_pct ?? null, lag_max_ms: b.lag_max_ms || 0 });
  }
  return out;
}

export default function LoadMonitorBlock() {
  const [data, setData] = useState(null);
  const [hours, setHours] = useState(24);
  const [live, setLive] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try { setData((await api.get("/admin/load-monitor", { params: { hours } })).data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  }, [hours]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!live) return undefined;
    const t = setInterval(load, REFRESH_MS);
    return () => clearInterval(t);
  }, [live, load]);

  const series = useMemo(() => (data ? fillMinutes(data.buckets, data.hours) : []), [data]);
  const s = data?.summary;
  const cur = data?.current;
  const tone = (v, warn, bad) => (v >= bad ? "border-destructive bg-destructive/10" : v >= warn ? "border-secondary bg-secondary/10" : "border-foreground bg-card");
  const btn = "btn-brutal px-3 py-1.5 font-display uppercase font-bold text-xs inline-flex items-center gap-1.5 disabled:opacity-50";

  return (
    <section className="bg-card border-2 border-foreground heavy-shadow" data-testid="load-monitor-block">
      <div className="p-5 border-b-2 border-foreground flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display font-black text-2xl uppercase tracking-tight inline-flex items-center gap-2"><Activity className="w-5 h-5" /> Belastingsmonitor <HelpButton section="beheer-belasting" label="Uitleg belastingsmonitor" size="sm" /></h2>
          <p className="text-xs text-muted-foreground mt-1">Responstijden, trage verzoeken, CPU en event-loop-vertraging van de backend per minuut (bewaard 24 uur).</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select value={hours} onChange={(e) => setHours(Number(e.target.value))} data-testid="load-monitor-hours"
                  className="border-2 border-foreground bg-background px-2 py-1.5 text-xs font-mono-num">
            {[1, 3, 6, 12, 24].map((h) => <option key={h} value={h}>{h} uur</option>)}
          </select>
          <button type="button" onClick={() => setLive((v) => !v)} className={`${btn} ${live ? "bg-positive text-foreground" : "bg-background text-muted-foreground"}`} data-testid="load-monitor-live" aria-pressed={live}>
            <Radio className={`w-3.5 h-3.5 ${live ? "animate-pulse" : ""}`} /> {live ? "Live · 30 s" : "Live uit"}
          </button>
          <button type="button" onClick={load} disabled={busy} className={`${btn} bg-background text-foreground`} data-testid="load-monitor-refresh"><RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} /> Ververs</button>
        </div>
      </div>

      {!data ? <div className="p-5 text-sm text-muted-foreground">Laden…</div> : (
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="load-monitor-kpis">
            <Kpi icon={<Gauge className="w-3.5 h-3.5" />} label="Verzoeken" value={s.requests.toLocaleString("nl-NL")} sub={`${s.minutes} min met verkeer`} testid="lm-kpi-requests" />
            <Kpi icon={<Timer className="w-3.5 h-3.5" />} label="Gem. responstijd" value={`${s.avg_ms} ms`} sub={`p95 piek ${s.p95_ms} ms`} cls={tone(s.p95_ms, 1000, 3000)} testid="lm-kpi-avg" />
            <Kpi icon={<AlertTriangle className="w-3.5 h-3.5" />} label="Foutpercentage" value={`${s.error_pct}%`} sub="HTTP 5xx" cls={tone(s.error_pct, 1, 5)} testid="lm-kpi-errors" />
            <Kpi icon={<Cpu className="w-3.5 h-3.5" />} label="CPU-piek backend" value={`${s.cpu_max_pct}%`} sub="van 1 core, per minuut" cls={tone(s.cpu_max_pct, 60, 90)} testid="lm-kpi-cpu" />
            <Kpi icon={<Activity className="w-3.5 h-3.5" />} label="Event-loop piek" value={`${s.lag_max_ms} ms`} sub="vertraging van de hoofdlus" cls={tone(s.lag_max_ms, 200, 1000)} testid="lm-kpi-lag" />
            <Kpi icon={<Timer className="w-3.5 h-3.5" />} label="Trage verzoeken" value={s.slow_count} sub={`> ${data.slow_threshold_ms / 1000} s`} cls={tone(s.slow_count, 1, 10)} testid="lm-kpi-slow" />
          </div>

          {cur && (
            <p className="text-[11px] text-muted-foreground font-mono-num" data-testid="load-monitor-current">
              Nu (lopende minuut): {cur.count} verzoeken · gem. {cur.avg_ms} ms · p95 {cur.p95_ms} ms · loop-lag {cur.lag_max_ms} ms{cur.rss_mb ? ` · geheugen ${cur.rss_mb} MB` : ""}
            </p>
          )}

          <LoadAlertCard />

          <div className="border-2 border-foreground p-3 bg-card" data-testid="load-monitor-chart-requests">
            <p className="font-display uppercase font-bold text-xs tracking-widest mb-2">Verzoeken per minuut &amp; p95-responstijd</p>
            <div style={{ width: "100%", height: 220 }}>
              <ResponsiveContainer>
                <ComposedChart data={series} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.08)" />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fontFamily: "monospace" }} minTickGap={40} />
                  <YAxis yAxisId="l" tick={{ fontSize: 10, fontFamily: "monospace" }} width={40} allowDecimals={false} />
                  <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 10, fontFamily: "monospace" }} width={56} tickFormatter={(v) => `${v}ms`} />
                  <Tooltip formatter={(v, name) => [name.includes("ms") ? `${v} ms` : v, name]} />
                  <Bar yAxisId="l" dataKey="count" name="Verzoeken" fill="#0047FF" />
                  <Bar yAxisId="l" dataKey="errors" name="Fouten (5xx)" fill="#FF5900" />
                  <Line yAxisId="r" dataKey="p95_ms" name="p95 ms" stroke="#000" strokeWidth={2} dot={false} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="border-2 border-foreground p-3 bg-card" data-testid="load-monitor-chart-cpu">
            <p className="font-display uppercase font-bold text-xs tracking-widest mb-2">CPU% backend &amp; event-loop-vertraging</p>
            <div style={{ width: "100%", height: 200 }}>
              <ResponsiveContainer>
                <ComposedChart data={series} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.08)" />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fontFamily: "monospace" }} minTickGap={40} />
                  <YAxis yAxisId="l" tick={{ fontSize: 10, fontFamily: "monospace" }} width={40} tickFormatter={(v) => `${v}%`} />
                  <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 10, fontFamily: "monospace" }} width={56} tickFormatter={(v) => `${v}ms`} />
                  <Tooltip formatter={(v, name) => [name.includes("%") ? `${v}%` : `${v} ms`, name]} />
                  <Area yAxisId="l" dataKey="cpu_pct" name="CPU %" fill="#00D05A" fillOpacity={0.25} stroke="#00D05A" connectNulls />
                  <Line yAxisId="r" dataKey="lag_max_ms" name="Loop-lag ms" stroke="#FF5900" strokeWidth={2} dot={false} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="border-2 border-foreground" data-testid="load-monitor-slow">
            <p className="font-display uppercase font-bold text-xs tracking-widest px-3 py-2 border-b-2 border-foreground">20 traagste verzoeken (&gt; {data.slow_threshold_ms / 1000} s)</p>
            {data.slow.length === 0 ? <div className="p-3 text-sm text-muted-foreground" data-testid="load-monitor-slow-empty">Geen trage verzoeken in deze periode.</div> : (
              <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-foreground text-background">
                  <tr>{["Tijdstip", "Methode", "Pad", "Status", "Duur"].map((h) => <th key={h} className="text-left px-3 py-2 font-display uppercase text-[10px] tracking-widest">{h}</th>)}</tr>
                </thead>
                <tbody>
                  {data.slow.map((r, i) => (
                    <tr key={`${r.at}-${i}`} className="border-t border-border even:bg-muted/30" data-testid={`load-monitor-slow-row-${i}`}>
                      <td className="px-3 py-2 font-mono-num text-xs whitespace-nowrap">{fmtWhen(r.at)}</td>
                      <td className="px-3 py-2 font-mono-num text-xs">{r.method}</td>
                      <td className="px-3 py-2 font-mono-num text-xs break-all min-w-[160px]">{r.path}</td>
                      <td className={`px-3 py-2 font-mono-num text-xs ${r.status >= 500 ? "text-destructive font-bold" : ""}`}>{r.status}</td>
                      <td className="px-3 py-2 font-mono-num text-xs font-bold whitespace-nowrap">{(r.ms / 1000).toFixed(1)} s</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

function Kpi({ icon, label, value, sub, cls = "border-foreground bg-card", testid }) {
  return (
    <div className={`border-2 p-3 ${cls}`} data-testid={testid}>
      <div className="stat-label flex items-center gap-1">{icon}{label}</div>
      <div className="font-display font-black text-xl mt-1 font-mono-num">{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}
